/**
 * Sample Shaders - Sample shader definitions and helpers
 */

// Sample categories
export const SAMPLE_CATEGORIES = {
  BASIC: 'basic',
  SDF: 'sdf',
  EFFECTS: 'effects',
  ADVANCED: 'advanced',
  MOBILE: 'mobile',
};

// Category display names
export const CATEGORY_LABELS = {
  [SAMPLE_CATEGORIES.BASIC]: 'Basic',
  [SAMPLE_CATEGORIES.SDF]: 'SDF',
  [SAMPLE_CATEGORIES.EFFECTS]: 'Effects',
  [SAMPLE_CATEGORIES.ADVANCED]: 'Advanced',
  [SAMPLE_CATEGORIES.MOBILE]: 'Mobile',
};

// Common shader header with uniforms and bindings
const SHADER_HEADER = `struct Uniforms {
  float2 resolution;
  float time;
  float _padding;
  float2 mouse;
};

[[vk::binding(0, 0)]]
ConstantBuffer<Uniforms> uniforms;

[[vk::binding(1, 0)]]
Texture2D<float4> iChannel0;

[[vk::binding(2, 0)]]
SamplerState iSampler;

[[vk::binding(3, 0)]]
Texture2D<float4> iChannel1;

[[vk::binding(4, 0)]]
Texture2D<float4> iChannel2;

[[vk::binding(5, 0)]]
Texture2D<float4> iChannel3;

[[vk::binding(6, 0)]]
Texture2D<float4> iBufferA;

[[vk::binding(7, 0)]]
Texture2D<float4> iBufferB;

[[vk::binding(8, 0)]]
Texture2D<float4> iBufferC;

[[vk::binding(9, 0)]]
Texture2D<float4> iBufferD;

struct VertexOutput {
  float4 position : SV_Position;
  float2 uv : TEXCOORD0;
};
`;

// Sample shader definitions
export const SAMPLES = [
  // ===== BASIC =====
  {
    id: 'gradient',
    name: 'Gradient',
    description: 'Simple animated color gradient',
    category: SAMPLE_CATEGORIES.BASIC,
    passes: {
      main: `${SHADER_HEADER}
[shader("fragment")]
float4 fragMain(VertexOutput input) : SV_Target {
  float2 uv = input.uv;

  // Animated gradient based on UV and time
  float3 color = float3(
    uv.x,
    uv.y,
    0.5 + 0.5 * sin(uniforms.time)
  );

  return float4(color, 1.0);
}`,
    },
  },
  {
    id: 'sine-waves',
    name: 'Sine Waves',
    description: 'Animated sine wave pattern',
    category: SAMPLE_CATEGORIES.BASIC,
    passes: {
      main: `${SHADER_HEADER}
[shader("fragment")]
float4 fragMain(VertexOutput input) : SV_Target {
  float2 uv = input.uv;
  float2 center = uv - 0.5;

  // Create animated sine wave pattern
  float wave = sin(center.x * 20.0 + uniforms.time * 2.0) * 0.1;
  wave += sin(center.y * 15.0 - uniforms.time * 1.5) * 0.1;

  float d = length(center) + wave;
  float3 color = float3(
    0.5 + 0.5 * sin(d * 10.0 - uniforms.time),
    0.5 + 0.5 * sin(d * 10.0 - uniforms.time + 2.094),
    0.5 + 0.5 * sin(d * 10.0 - uniforms.time + 4.188)
  );

  return float4(color, 1.0);
}`,
    },
  },
  {
    id: 'circle',
    name: 'Circle',
    description: 'Basic circle with smooth edge',
    category: SAMPLE_CATEGORIES.BASIC,
    passes: {
      main: `${SHADER_HEADER}
[shader("fragment")]
float4 fragMain(VertexOutput input) : SV_Target {
  float2 uv = input.uv;
  float2 center = uv - 0.5;

  // Correct aspect ratio
  float aspect = uniforms.resolution.x / uniforms.resolution.y;
  center.x *= aspect;

  // Distance from center
  float d = length(center);

  // Animated radius
  float radius = 0.3 + 0.05 * sin(uniforms.time * 2.0);

  // Smooth circle
  float circle = smoothstep(radius + 0.01, radius - 0.01, d);

  // Color
  float3 bgColor = float3(0.1, 0.1, 0.15);
  float3 circleColor = float3(0.3, 0.6, 1.0);
  float3 color = lerp(bgColor, circleColor, circle);

  return float4(color, 1.0);
}`,
    },
  },

  // ===== SDF =====
  {
    id: 'sdf-shapes',
    name: '2D Shapes',
    description: 'Basic 2D SDF shapes',
    category: SAMPLE_CATEGORIES.SDF,
    passes: {
      main: `${SHADER_HEADER}
// SDF helper functions
float sdCircle(float2 p, float r) {
  return length(p) - r;
}

float sdBox(float2 p, float2 b) {
  float2 d = abs(p) - b;
  return length(max(d, 0.0)) + min(max(d.x, d.y), 0.0);
}

float sdRoundedBox(float2 p, float2 b, float r) {
  float2 q = abs(p) - b + r;
  return min(max(q.x, q.y), 0.0) + length(max(q, 0.0)) - r;
}

[shader("fragment")]
float4 fragMain(VertexOutput input) : SV_Target {
  float2 uv = input.uv;
  float2 p = (uv - 0.5) * 2.0;
  float aspect = uniforms.resolution.x / uniforms.resolution.y;
  p.x *= aspect;

  // Animate position
  float t = uniforms.time;

  // Circle
  float d1 = sdCircle(p - float2(-0.8, 0.0), 0.25);

  // Box
  float d2 = sdBox(p - float2(0.0, 0.0), float2(0.2, 0.2));

  // Rounded box
  float d3 = sdRoundedBox(p - float2(0.8, 0.0), float2(0.2, 0.2), 0.08);

  // Combine shapes
  float d = min(min(d1, d2), d3);

  // Coloring
  float3 color = float3(0.1, 0.1, 0.15);

  // Glow effect
  float glow = 0.02 / abs(d);
  color += float3(0.3, 0.6, 1.0) * glow * 0.5;

  // Fill shapes
  if (d < 0.0) {
    color = float3(0.3, 0.6, 1.0);
  }

  return float4(color, 1.0);
}`,
    },
  },
  {
    id: 'raymarching-basics',
    name: 'Raymarching Basics',
    description: '3D raymarching with a simple sphere',
    category: SAMPLE_CATEGORIES.SDF,
    passes: {
      main: `${SHADER_HEADER}
float sdSphere(float3 p, float r) {
  return length(p) - r;
}

float3 getNormal(float3 p) {
  float2 e = float2(0.001, 0.0);
  return normalize(float3(
    sdSphere(p + e.xyy, 1.0) - sdSphere(p - e.xyy, 1.0),
    sdSphere(p + e.yxy, 1.0) - sdSphere(p - e.yxy, 1.0),
    sdSphere(p + e.yyx, 1.0) - sdSphere(p - e.yyx, 1.0)
  ));
}

[shader("fragment")]
float4 fragMain(VertexOutput input) : SV_Target {
  float2 uv = input.uv;
  float2 p = (uv - 0.5) * 2.0;
  p.y = -p.y;  // Flip Y for correct orientation
  float aspect = uniforms.resolution.x / uniforms.resolution.y;
  p.x *= aspect;

  // Camera setup
  float3 ro = float3(0.0, 0.0, -3.0);
  float3 rd = normalize(float3(p, 1.0));

  // Raymarching
  float t = 0.0;
  float3 color = float3(0.05, 0.05, 0.1);

  for (int i = 0; i < 64; i++) {
    float3 pos = ro + rd * t;
    float d = sdSphere(pos, 1.0);

    if (d < 0.001) {
      // Hit - calculate lighting
      float3 normal = getNormal(pos);
      float3 lightDir = normalize(float3(1.0, 1.0, -1.0));
      float diff = max(dot(normal, lightDir), 0.0);
      float spec = pow(max(dot(reflect(-lightDir, normal), -rd), 0.0), 32.0);

      color = float3(0.2, 0.5, 0.9) * (0.2 + diff * 0.6) + spec * 0.5;
      break;
    }

    if (t > 10.0) break;
    t += d;
  }

  return float4(color, 1.0);
}`,
    },
  },

  // ===== EFFECTS =====
  {
    id: 'glow',
    name: 'Glow Effect',
    description: 'Neon glow effect on shapes',
    category: SAMPLE_CATEGORIES.EFFECTS,
    passes: {
      main: `${SHADER_HEADER}
[shader("fragment")]
float4 fragMain(VertexOutput input) : SV_Target {
  float2 uv = input.uv;
  float2 p = (uv - 0.5) * 2.0;
  float aspect = uniforms.resolution.x / uniforms.resolution.y;
  p.x *= aspect;

  float t = uniforms.time;

  // Animated ring
  float radius = 0.5 + 0.1 * sin(t * 2.0);
  float d = abs(length(p) - radius);

  // Multiple glow layers
  float glow = 0.0;
  glow += 0.005 / (d + 0.005);
  glow += 0.01 / (d + 0.02);
  glow += 0.02 / (d + 0.05);

  // Color gradient based on angle
  float angle = atan2(p.y, p.x) + t;
  float3 color1 = float3(1.0, 0.2, 0.5);
  float3 color2 = float3(0.2, 0.5, 1.0);
  float3 glowColor = lerp(color1, color2, 0.5 + 0.5 * sin(angle * 3.0));

  float3 color = glowColor * glow;

  // Add subtle background
  color += float3(0.02, 0.02, 0.05);

  return float4(color, 1.0);
}`,
    },
  },
  {
    id: 'noise-pattern',
    name: 'Noise Pattern',
    description: 'Procedural noise visualization',
    category: SAMPLE_CATEGORIES.EFFECTS,
    passes: {
      main: `${SHADER_HEADER}
// Simple hash function
float hash(float2 p) {
  return frac(sin(dot(p, float2(127.1, 311.7))) * 43758.5453);
}

// Value noise
float noise(float2 p) {
  float2 i = floor(p);
  float2 f = frac(p);
  f = f * f * (3.0 - 2.0 * f);

  float a = hash(i);
  float b = hash(i + float2(1.0, 0.0));
  float c = hash(i + float2(0.0, 1.0));
  float d = hash(i + float2(1.0, 1.0));

  return lerp(lerp(a, b, f.x), lerp(c, d, f.x), f.y);
}

// Fractal Brownian Motion
float fbm(float2 p) {
  float value = 0.0;
  float amplitude = 0.5;
  for (int i = 0; i < 5; i++) {
    value += amplitude * noise(p);
    p *= 2.0;
    amplitude *= 0.5;
  }
  return value;
}

[shader("fragment")]
float4 fragMain(VertexOutput input) : SV_Target {
  float2 uv = input.uv;
  float t = uniforms.time * 0.5;

  // Animated noise
  float n = fbm(uv * 5.0 + float2(t, t * 0.7));

  // Color mapping
  float3 color1 = float3(0.1, 0.2, 0.4);
  float3 color2 = float3(0.8, 0.4, 0.2);
  float3 color3 = float3(1.0, 0.9, 0.7);

  float3 color;
  if (n < 0.5) {
    color = lerp(color1, color2, n * 2.0);
  } else {
    color = lerp(color2, color3, (n - 0.5) * 2.0);
  }

  return float4(color, 1.0);
}`,
    },
  },
  {
    id: 'blur-multipass',
    name: 'Blur',
    description: 'Multi-pass Gaussian blur effect',
    category: SAMPLE_CATEGORIES.EFFECTS,
    passes: {
      main: `${SHADER_HEADER}
[shader("fragment")]
float4 fragMain(VertexOutput input) : SV_Target {
  float2 uv = input.uv;

  // Read blurred result from Buffer A
  float4 blurred = iBufferA.Sample(iSampler, uv);

  return blurred;
}`,
      bufferA: `${SHADER_HEADER}
[shader("fragment")]
float4 fragMain(VertexOutput input) : SV_Target {
  float2 uv = input.uv;
  float2 texel = 1.0 / uniforms.resolution;

  // Source: animated pattern
  float t = uniforms.time;
  float2 p = (uv - 0.5) * 2.0;
  float d = length(p);
  float angle = atan2(p.y, p.x);

  // Create sharp source pattern
  float pattern = 0.0;
  for (int i = 0; i < 6; i++) {
    float a = angle + float(i) * 1.047 + t;
    float ray = smoothstep(0.1, 0.0, abs(sin(a * 3.0)));
    pattern += ray * smoothstep(0.8, 0.2, d);
  }

  float3 sourceColor = float3(1.0, 0.5, 0.2) * pattern;
  sourceColor += float3(0.2, 0.5, 1.0) * smoothstep(0.3, 0.0, d);

  // Simple box blur (9 samples)
  float4 prev = iBufferA.Sample(iSampler, uv);
  float4 color = float4(0.0, 0.0, 0.0, 0.0);

  for (int x = -1; x <= 1; x++) {
    for (int y = -1; y <= 1; y++) {
      float2 offset = float2(float(x), float(y)) * texel * 2.0;
      color += iBufferA.Sample(iSampler, uv + offset);
    }
  }
  color /= 9.0;

  // Blend with source
  float4 source = float4(sourceColor, 1.0);
  return lerp(color, source, 0.3);
}`,
    },
  },

  // ===== ADVANCED =====
  {
    id: 'raymarching-scene',
    name: 'Raymarching Scene',
    description: '3D scene with multiple objects',
    category: SAMPLE_CATEGORIES.ADVANCED,
    passes: {
      main: `${SHADER_HEADER}
float sdSphere(float3 p, float r) {
  return length(p) - r;
}

float sdBox(float3 p, float3 b) {
  float3 q = abs(p) - b;
  return length(max(q, 0.0)) + min(max(q.x, max(q.y, q.z)), 0.0);
}

float sdPlane(float3 p) {
  return p.y + 1.0;
}

float2 scene(float3 p) {
  float t = uniforms.time;

  // Floor
  float floor = sdPlane(p);
  float2 res = float2(floor, 1.0);

  // Central sphere
  float sphere = sdSphere(p - float3(0.0, 0.0, 0.0), 0.8);
  if (sphere < res.x) res = float2(sphere, 2.0);

  // Orbiting boxes
  for (int i = 0; i < 3; i++) {
    float angle = t + float(i) * 2.094;
    float3 boxPos = float3(cos(angle) * 1.5, sin(t * 2.0 + float(i)) * 0.3, sin(angle) * 1.5);
    float box = sdBox(p - boxPos, float3(0.2, 0.2, 0.2));
    if (box < res.x) res = float2(box, 3.0);
  }

  return res;
}

float3 getNormal(float3 p) {
  float2 e = float2(0.001, 0.0);
  return normalize(float3(
    scene(p + e.xyy).x - scene(p - e.xyy).x,
    scene(p + e.yxy).x - scene(p - e.yxy).x,
    scene(p + e.yyx).x - scene(p - e.yyx).x
  ));
}

float softShadow(float3 ro, float3 rd, float mint, float maxt, float k) {
  float res = 1.0;
  float t = mint;
  for (int i = 0; i < 32; i++) {
    float h = scene(ro + rd * t).x;
    res = min(res, k * h / t);
    t += clamp(h, 0.02, 0.1);
    if (h < 0.001 || t > maxt) break;
  }
  return clamp(res, 0.0, 1.0);
}

[shader("fragment")]
float4 fragMain(VertexOutput input) : SV_Target {
  float2 uv = input.uv;
  float2 p = (uv - 0.5) * 2.0;
  p.y = -p.y;  // Flip Y for correct orientation
  float aspect = uniforms.resolution.x / uniforms.resolution.y;
  p.x *= aspect;

  // Camera
  float3 ro = float3(0.0, 1.5, -4.0);
  float3 rd = normalize(float3(p, 1.5));

  // Raymarching
  float t = 0.0;
  float2 hit = float2(100.0, 0.0);

  for (int i = 0; i < 128; i++) {
    float3 pos = ro + rd * t;
    hit = scene(pos);
    if (hit.x < 0.001 || t > 20.0) break;
    t += hit.x;
  }

  float3 color = float3(0.1, 0.15, 0.2);

  if (t < 20.0) {
    float3 pos = ro + rd * t;
    float3 normal = getNormal(pos);
    float3 lightDir = normalize(float3(1.0, 2.0, -1.0));

    // Material colors
    float3 matColor;
    if (hit.y < 1.5) matColor = float3(0.3, 0.3, 0.35);
    else if (hit.y < 2.5) matColor = float3(0.2, 0.5, 0.9);
    else matColor = float3(0.9, 0.4, 0.2);

    // Lighting
    float diff = max(dot(normal, lightDir), 0.0);
    float shadow = softShadow(pos + normal * 0.01, lightDir, 0.02, 10.0, 8.0);
    float spec = pow(max(dot(reflect(-lightDir, normal), -rd), 0.0), 32.0);

    color = matColor * (0.15 + diff * shadow * 0.7) + spec * shadow * 0.3;
  }

  // Fog
  color = lerp(color, float3(0.1, 0.15, 0.2), 1.0 - exp(-t * 0.05));

  return float4(color, 1.0);
}`,
    },
  },
  {
    id: 'mandelbrot',
    name: 'Mandelbrot',
    description: 'Animated Mandelbrot fractal zoom',
    category: SAMPLE_CATEGORIES.ADVANCED,
    passes: {
      main: `${SHADER_HEADER}
[shader("fragment")]
float4 fragMain(VertexOutput input) : SV_Target {
  float2 uv = input.uv;
  float aspect = uniforms.resolution.x / uniforms.resolution.y;

  // Animated zoom
  float t = uniforms.time * 0.3;
  float zoom = pow(2.0, -t);

  // Interesting location to zoom into
  float2 center = float2(-0.745, 0.186);

  // Map UV to complex plane
  float2 c = (uv - 0.5) * float2(aspect, 1.0) * 3.0 * zoom + center;

  // Mandelbrot iteration
  float2 z = float2(0.0, 0.0);
  int maxIter = 256;
  int iter;

  for (iter = 0; iter < maxIter; iter++) {
    // z = z^2 + c
    z = float2(z.x * z.x - z.y * z.y, 2.0 * z.x * z.y) + c;
    if (dot(z, z) > 4.0) break;
  }

  // Smooth iteration count
  float smoothIter = float(iter);
  if (iter < maxIter) {
    smoothIter = float(iter) - log2(log2(dot(z, z))) + 4.0;
  }

  // Coloring
  float3 color;
  if (iter == maxIter) {
    color = float3(0.0, 0.0, 0.0);
  } else {
    float hue = smoothIter * 0.03 + t * 0.5;
    // HSV to RGB
    float3 k = float3(1.0, 2.0/3.0, 1.0/3.0);
    float3 p = abs(frac(float3(hue, hue, hue) + k) * 6.0 - 3.0);
    color = lerp(float3(1.0, 1.0, 1.0), clamp(p - 1.0, 0.0, 1.0), 0.8);
    color *= 0.8 + 0.2 * sin(smoothIter * 0.1);
  }

  return float4(color, 1.0);
}`,
    },
  },
  {
    id: 'feedback-trail',
    name: 'Feedback Trail',
    description: 'Multi-pass feedback effect with trails',
    category: SAMPLE_CATEGORIES.ADVANCED,
    passes: {
      main: `${SHADER_HEADER}
[shader("fragment")]
float4 fragMain(VertexOutput input) : SV_Target {
  float2 uv = input.uv;

  // Read accumulated buffer
  float4 trail = iBufferA.Sample(iSampler, uv);

  // Add some contrast and color grading
  float3 color = trail.rgb;
  color = pow(color, float3(0.9, 0.9, 0.9));

  return float4(color, 1.0);
}`,
      bufferA: `${SHADER_HEADER}
[shader("fragment")]
float4 fragMain(VertexOutput input) : SV_Target {
  float2 uv = input.uv;
  float t = uniforms.time;

  // Sample previous frame with slight offset for motion
  float2 center = float2(0.5, 0.5);
  float2 dir = normalize(uv - center);
  float dist = length(uv - center);

  // Spiral distortion
  float angle = dist * 2.0 + t * 0.5;
  float2 offset = float2(cos(angle), sin(angle)) * 0.002;

  // Read previous with fade and slight zoom
  float2 zoomUV = (uv - 0.5) * 0.998 + 0.5;
  float4 prev = iBufferA.Sample(iSampler, zoomUV + offset);
  prev *= 0.97; // Fade factor

  // Draw animated particle
  float2 p = (uv - 0.5) * 2.0;
  float aspect = uniforms.resolution.x / uniforms.resolution.y;
  p.x *= aspect;

  // Moving point
  float2 particlePos = float2(
    cos(t * 1.7) * 0.6,
    sin(t * 2.3) * 0.4
  );

  float d = length(p - particlePos);
  float glow = 0.02 / (d + 0.02);

  // Particle color cycles
  float3 particleColor = float3(
    0.5 + 0.5 * sin(t * 1.0),
    0.5 + 0.5 * sin(t * 1.3 + 2.0),
    0.5 + 0.5 * sin(t * 1.7 + 4.0)
  );

  float3 newColor = particleColor * glow;

  // Blend with previous
  float3 color = max(prev.rgb, newColor);

  return float4(color, 1.0);
}`,
    },
  },

  // ===== MOBILE (Camera Effects) =====
  {
    id: 'camera-glitch',
    name: 'Glitch',
    description: 'Digital glitch effect on camera (set iChannel0 to Camera)',
    category: SAMPLE_CATEGORIES.MOBILE,
    passes: {
      main: `${SHADER_HEADER}
// Hash function for noise
float hash(float n) {
  return frac(sin(n) * 43758.5453);
}

float hash2(float2 p) {
  return frac(sin(dot(p, float2(127.1, 311.7))) * 43758.5453);
}

[shader("fragment")]
float4 fragMain(VertexOutput input) : SV_Target {
  float2 uv = input.uv;
  float t = uniforms.time;

  // Glitch timing
  float glitchIntensity = step(0.95, hash(floor(t * 10.0)));
  float lineGlitch = step(0.98, hash(floor(t * 20.0)));

  // Horizontal displacement
  float displacement = 0.0;
  if (glitchIntensity > 0.0) {
    float block = floor(uv.y * 20.0);
    displacement = (hash(block + t) - 0.5) * 0.1 * glitchIntensity;
  }

  // Line shift
  if (lineGlitch > 0.0) {
    float scanline = floor(uv.y * 100.0);
    if (hash(scanline + floor(t * 5.0)) > 0.8) {
      displacement += (hash(scanline) - 0.5) * 0.2;
    }
  }

  // RGB split
  float rgbSplit = 0.005 + glitchIntensity * 0.02;

  float2 uvR = uv + float2(displacement + rgbSplit, 0.0);
  float2 uvG = uv + float2(displacement, 0.0);
  float2 uvB = uv + float2(displacement - rgbSplit, 0.0);

  // Sample camera with RGB split
  float r = iChannel0.Sample(iSampler, uvR).r;
  float g = iChannel0.Sample(iSampler, uvG).g;
  float b = iChannel0.Sample(iSampler, uvB).b;

  float3 color = float3(r, g, b);

  // Scanlines
  float scanline = sin(uv.y * uniforms.resolution.y * 1.5) * 0.04;
  color -= scanline;

  // Random color blocks
  if (glitchIntensity > 0.0 && hash2(floor(uv * 10.0) + t) > 0.95) {
    color = float3(hash(t), hash(t + 1.0), hash(t + 2.0));
  }

  return float4(color, 1.0);
}`,
    },
  },
  {
    id: 'camera-pixelate',
    name: 'Pixelate',
    description: 'Retro pixel art effect on camera (set iChannel0 to Camera)',
    category: SAMPLE_CATEGORIES.MOBILE,
    passes: {
      main: `${SHADER_HEADER}
[shader("fragment")]
float4 fragMain(VertexOutput input) : SV_Target {
  float2 uv = input.uv;

  // Pixel size (adjustable)
  float pixelSize = 8.0 + 8.0 * (0.5 + 0.5 * sin(uniforms.time * 0.5));

  // Pixelate UV coordinates
  float2 pixelUV = floor(uv * uniforms.resolution / pixelSize) * pixelSize / uniforms.resolution;

  // Sample camera
  float4 cam = iChannel0.Sample(iSampler, pixelUV);

  // Reduce color palette (posterize)
  float levels = 8.0;
  float3 color = floor(cam.rgb * levels) / levels;

  // Add subtle grid lines
  float2 grid = frac(uv * uniforms.resolution / pixelSize);
  float gridLine = step(0.9, max(grid.x, grid.y)) * 0.1;
  color -= gridLine;

  return float4(color, 1.0);
}`,
    },
  },
  {
    id: 'camera-thermal',
    name: 'Thermal',
    description: 'Thermal camera effect (set iChannel0 to Camera)',
    category: SAMPLE_CATEGORIES.MOBILE,
    passes: {
      main: `${SHADER_HEADER}
[shader("fragment")]
float4 fragMain(VertexOutput input) : SV_Target {
  float2 uv = input.uv;

  // Sample camera
  float4 cam = iChannel0.Sample(iSampler, uv);

  // Calculate luminance (heat)
  float heat = dot(cam.rgb, float3(0.299, 0.587, 0.114));

  // Thermal color gradient: black -> blue -> purple -> red -> orange -> yellow -> white
  float3 color;
  if (heat < 0.15) {
    color = lerp(float3(0.0, 0.0, 0.1), float3(0.0, 0.0, 0.5), heat / 0.15);
  } else if (heat < 0.3) {
    color = lerp(float3(0.0, 0.0, 0.5), float3(0.5, 0.0, 0.5), (heat - 0.15) / 0.15);
  } else if (heat < 0.5) {
    color = lerp(float3(0.5, 0.0, 0.5), float3(1.0, 0.0, 0.0), (heat - 0.3) / 0.2);
  } else if (heat < 0.7) {
    color = lerp(float3(1.0, 0.0, 0.0), float3(1.0, 0.5, 0.0), (heat - 0.5) / 0.2);
  } else if (heat < 0.85) {
    color = lerp(float3(1.0, 0.5, 0.0), float3(1.0, 1.0, 0.0), (heat - 0.7) / 0.15);
  } else {
    color = lerp(float3(1.0, 1.0, 0.0), float3(1.0, 1.0, 1.0), (heat - 0.85) / 0.15);
  }

  // Add noise for thermal camera feel
  float noise = frac(sin(dot(uv + uniforms.time * 0.01, float2(12.9898, 78.233))) * 43758.5453);
  color += (noise - 0.5) * 0.05;

  return float4(color, 1.0);
}`,
    },
  },
  {
    id: 'camera-feedback',
    name: 'Feedback Loop',
    description: 'Ghostly frame feedback effect (set iChannel0 to Camera)',
    category: SAMPLE_CATEGORIES.MOBILE,
    passes: {
      main: `${SHADER_HEADER}
[shader("fragment")]
float4 fragMain(VertexOutput input) : SV_Target {
  float2 uv = input.uv;

  // Read accumulated buffer
  float4 feedback = iBufferA.Sample(iSampler, uv);

  return float4(feedback.rgb, 1.0);
}`,
      bufferA: `${SHADER_HEADER}
[shader("fragment")]
float4 fragMain(VertexOutput input) : SV_Target {
  float2 uv = input.uv;

  // Sample current camera frame
  float4 current = iChannel0.Sample(iSampler, uv);

  // Sample previous accumulated frame
  float4 prev = iBufferA.Sample(iSampler, uv);

  // Blend: keep previous frames longer
  float fadeRate = 0.92;
  float3 color = lerp(current.rgb, prev.rgb, fadeRate);

  return float4(color, 1.0);
}`,
    },
  },
  {
    id: 'camera-kaleidoscope',
    name: 'Kaleidoscope',
    description: 'Mirror kaleidoscope effect (set iChannel0 to Camera)',
    category: SAMPLE_CATEGORIES.MOBILE,
    passes: {
      main: `${SHADER_HEADER}
[shader("fragment")]
float4 fragMain(VertexOutput input) : SV_Target {
  float2 uv = input.uv;
  float t = uniforms.time;

  // Center and convert to polar
  float2 center = uv - 0.5;
  float aspect = uniforms.resolution.x / uniforms.resolution.y;
  center.x *= aspect;

  float r = length(center);
  float a = atan2(center.y, center.x);

  // Number of segments (animated)
  float segments = 6.0;

  // Rotate over time
  a += t * 0.3;

  // Mirror the angle within segments
  float segmentAngle = 3.14159 * 2.0 / segments;
  a = abs(((a % segmentAngle) + segmentAngle) % segmentAngle);
  if (int(floor((atan2(center.y, center.x) + t * 0.3) / segmentAngle)) % 2 == 1) {
    a = segmentAngle - a;
  }

  // Convert back to cartesian
  float2 kaleidoUV;
  kaleidoUV.x = r * cos(a) / aspect + 0.5;
  kaleidoUV.y = r * sin(a) + 0.5;

  // Sample camera
  float4 cam = iChannel0.Sample(iSampler, kaleidoUV);

  // Add slight color shift based on angle
  float3 color = cam.rgb;
  color *= 0.8 + 0.2 * sin(a * segments + t);

  return float4(color, 1.0);
}`,
    },
  },
];

/**
 * Get samples filtered by category
 * @param {string} category - Category ID
 * @returns {Array} Filtered samples
 */
export function getSamplesByCategory(category) {
  return SAMPLES.filter((sample) => sample.category === category);
}

/**
 * Get a sample by ID
 * @param {string} id - Sample ID
 * @returns {Object|undefined} Sample or undefined
 */
export function getSampleById(id) {
  return SAMPLES.find((sample) => sample.id === id);
}

/**
 * Check if a sample uses multiple passes
 * @param {Object} sample - Sample object
 * @returns {boolean}
 */
export function isMultiPass(sample) {
  return Object.keys(sample.passes).length > 1;
}

/**
 * Get all category IDs in order
 * @returns {string[]}
 */
export function getCategoryOrder() {
  return [
    SAMPLE_CATEGORIES.BASIC,
    SAMPLE_CATEGORIES.SDF,
    SAMPLE_CATEGORIES.EFFECTS,
    SAMPLE_CATEGORIES.ADVANCED,
    SAMPLE_CATEGORIES.MOBILE,
  ];
}
